import * as React from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { RATIO, SEMANTIC_TAG, TypographySize } from 'mm-theme-configuration/dist/consts';
import { useTheme } from '../../../../theming/useTheme';
import { BaseUnit } from '../../../../theming/baseUnitDefinition';
import { CardComponentDataProps, CardComponentPropsFromLayout } from '../cards.utils';
import { WrapperForCard } from '../WrapperForCard';
import { Link } from '../../link/Link';
import { ConditionalVideoWrap } from '../../ConditionalVideoWrap';
import { Image } from '../../image/Image';
import { CARD_CAPTION_CONTAINER_TYPES, CardCaptionContainer } from '../../CardCaptionContainer';
import { semanticCSS } from '../../../../typography/semanticTags';
import { createStyleForBoxShadow } from '../../../../theming/boxShadow';
import { MEDIA_BREAKPOINTS } from '../../../../mediaQueries.const';
import { DynamicSemanticTag } from '../../../../typography/semanticTags/DynamicSemanticTag';
import { createImageAspectRatios } from '../../image/image.utils';

const numberOfLines = 3;
export const componentConfigThemeKey = 'horizontalCard';

export interface HorizontalCardThemeProps {
  cardBackgroundColor: string;
  cardTitleColorTypeNormal: string;
  cardTitleColorTypeHover: string;
  cardTitleColorTypeActive: string;
  baseUnit: BaseUnit;
  borderRadius: number;
  boxShadowTypeNormal: string;
  boxShadowTypeActive: string;
  boxShadowTypeHover: string;
  borderWidthTypeNormal: number;
  borderWidthTypeHover: number;
  borderWidthTypeActive: number;
  borderColorTypeNormal: string;
  borderColorTypeHover: string;
  borderColorTypeActive: string;
  aspectRatioLarge: RATIO;
  aspectRatioMedium: RATIO;
  aspectRatioSmall: RATIO;
  titleFontSizeLarge: TypographySize;
  titleFontSizeMedium: TypographySize;
  titleFontSizeSmall: TypographySize;
  shouldReverseImageAlignment: boolean;
  showDate: boolean;
  dateFontSizeLarge: TypographySize;
  dateFontSizeMedium: TypographySize;
  dateFontSizeSmall: TypographySize;
  dateColor: string;
  pipeColor: string;
  showAuthor: boolean;
  authorFontSizeLarge: TypographySize;
  authorFontSizeMedium: TypographySize;
  authorFontSizeSmall: TypographySize;
  authorColor: string;
  wrapperGapLarge: number;
  wrapperGapMedium: number;
  wrapperGapSmall: number;
  verticalGapLarge: number;
  verticalGapMedium: number;
  verticalGapSmall: number;
  horizontalGapLarge: number;
  horizontalGapMedium: number;
  horizontalGapSmall: number;
  titleSemanticTag: SEMANTIC_TAG;
  dateSemanticTag: SEMANTIC_TAG;
  authorSemanticTag: SEMANTIC_TAG;
  cardProportionsLarge: RATIO;
  cardProportionsMedium: RATIO;
  cardProportionsSmall: RATIO;
}
type HorizontalCardProps = CardComponentDataProps & CardComponentPropsFromLayout;

const createStyle = (cardBackgroundColor: string, cardTextColorTypeNormal: string, cardTextColorTypeHover: string, cardTextColorTypeActive: string, baseUnit: BaseUnit | null, borderRadius: number, shouldReverseImageAlignment: boolean, cardProportionsLarge: RATIO, cardProportionsMedium: RATIO, cardProportionsSmall: RATIO) => {
  return StyleSheet.create({
    text: {
      overflow: 'hidden',
    },
    wrapper: {
      width: '100%',
      height: '100%',
      textDecoration: 'none',
      color: cardTextColorTypeNormal,
      backgroundColor: cardBackgroundColor,
      '-webkit-mask-image': '-webkit-radial-gradient(white, black)',
      borderRadius: `${borderRadius}px`,
      position: 'relative',
      ':hover': {
        color: cardTextColorTypeHover,
      },
      ':active': {
        color: cardTextColorTypeActive,
      },
      display: 'grid',
      [MEDIA_BREAKPOINTS.large]: {
        gridTemplateColumns: shouldReverseImageAlignment ? `${cardProportionsLarge.y}fr ${cardProportionsLarge.x}fr` : `${cardProportionsLarge.x}fr ${cardProportionsLarge.y}fr`,
      },
      [MEDIA_BREAKPOINTS.medium]: {
        gridTemplateColumns: shouldReverseImageAlignment ? `${cardProportionsMedium.y}fr ${cardProportionsMedium.x}fr` : `${cardProportionsMedium.x}fr ${cardProportionsMedium.y}fr`,
      },
      [MEDIA_BREAKPOINTS.small]: {
        gridTemplateColumns: shouldReverseImageAlignment ? `${cardProportionsSmall.y}fr ${cardProportionsSmall.x}fr` : `${cardProportionsSmall.x}fr ${cardProportionsSmall.y}fr`,
      },
    },
  });
};

export const HorizontalCard: React.FunctionComponent<HorizontalCardProps> = props => {
  const {
    title,
    image,
    articleUrl,
    coverType,
    lazyLoad = true,
    imageWidths,
    imageHeightCalculationMethods,
  } = props;
  const {
    baseUnit,
    cardBackgroundColor,
    cardTitleColorTypeNormal,
    cardTitleColorTypeHover,
    cardTitleColorTypeActive,
    boxShadowTypeNormal,
    boxShadowTypeActive,
    boxShadowTypeHover,
    borderRadius,
    borderColorTypeActive,
    borderColorTypeHover,
    borderColorTypeNormal,
    borderWidthTypeActive,
    borderWidthTypeHover,
    borderWidthTypeNormal,
    titleFontSizeLarge,
    titleFontSizeSmall,
    titleFontSizeMedium,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    showAuthor,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    showDate,
    aspectRatioLarge,
    aspectRatioMedium,
    aspectRatioSmall,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    authorColor,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    authorFontSizeLarge,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    authorFontSizeMedium,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    authorFontSizeSmall,
    shouldReverseImageAlignment,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    dateColor,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    dateFontSizeLarge,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    dateFontSizeMedium,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    dateFontSizeSmall,
    horizontalGapLarge,
    horizontalGapMedium,
    horizontalGapSmall,
    verticalGapLarge,
    verticalGapMedium,
    verticalGapSmall,
    wrapperGapLarge,
    wrapperGapMedium,
    wrapperGapSmall,
    titleSemanticTag,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    dateSemanticTag,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    authorSemanticTag,
    cardProportionsLarge,
    cardProportionsMedium,
    cardProportionsSmall,
  } = useTheme<HorizontalCardThemeProps>(componentConfigThemeKey);
  const borderBoxShadow = {
    borderColorTypeActive,
    borderColorTypeHover,
    borderColorTypeNormal,
    borderWidthTypeActive,
    borderWidthTypeHover,
    borderWidthTypeNormal,
  };
  const boxShadowStyle = createStyleForBoxShadow({ boxShadowTypeNormal, boxShadowTypeHover, boxShadowTypeActive, borderBoxShadow });
  const style = createStyle(cardBackgroundColor, cardTitleColorTypeNormal, cardTitleColorTypeHover, cardTitleColorTypeActive, baseUnit, borderRadius, shouldReverseImageAlignment, cardProportionsLarge, cardProportionsMedium, cardProportionsSmall);
  const ImageElement: React.FunctionComponent = () => (
    <ConditionalVideoWrap shouldWarp={coverType === 'video'}>
      <Image {...image} lazyLoad={lazyLoad} imageWidths={imageWidths} imageAspectRatios={createImageAspectRatios(aspectRatioSmall, aspectRatioMedium, aspectRatioLarge)} imageHeightCalculationMethods={imageHeightCalculationMethods} />
    </ConditionalVideoWrap>
  );
  const TextElement: React.FunctionComponent = () => (
    <CardCaptionContainer
      numberOfLines={numberOfLines}
      type={CARD_CAPTION_CONTAINER_TYPES.HORIZONTAL}
      horizontalMarginLarge={horizontalGapLarge}
      horizontalMarginMedium={horizontalGapMedium}
      horizontalMarginSmall={horizontalGapSmall}
      verticalMarginLarge={verticalGapLarge}
      verticalMarginMedium={verticalGapMedium}
      verticalMarginSmall={verticalGapSmall}
    >
      <DynamicSemanticTag semanticTag={titleSemanticTag} fontSizeLarge={titleFontSizeLarge} fontSizeMedium={titleFontSizeMedium} fontSizeSmall={titleFontSizeSmall} styles={semanticCSS(style.text)}>
        {title}
      </DynamicSemanticTag>
    </CardCaptionContainer>
  );
  const elements = [<ImageElement key="image" />, <TextElement key="text" />];

  return (
    <WrapperForCard
      boxShadowStyle={boxShadowStyle}
      borderRadius={borderRadius}
      paddingLarge={wrapperGapLarge}
      paddingMedium={wrapperGapMedium}
      paddingSmall={wrapperGapSmall}
    >
      <Link className={css(style.wrapper, boxShadowStyle.style)} href={articleUrl}>
        {shouldReverseImageAlignment ? elements.reverse() : elements}
      </Link>
    </WrapperForCard>
  );
};
